/* NV feature hero: entrance reveal plus a 3D depth effect.
   - desktop: the photo plane tilts towards the pointer; photo, dust and gold frame move at different depths
   - touch: the same depth driven by scroll position
   - drifting particles: gold dust and saffron strands (kumkum), amber motes (espresso)
   Everything pauses off-screen and is skipped under prefers-reduced-motion. */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function init(root) {
    if (root.__nvfh) return;
    root.__nvfh = true;

    var io = 'IntersectionObserver' in window;
    if (!io) root.classList.add('is-in');
    else new IntersectionObserver(function (es, o) {
      es.forEach(function (e) { if (e.isIntersecting) { root.classList.add('is-in'); o.disconnect(); } });
    }, { threshold: 0.2 }).observe(root);

    if (reduce) return;

    var media = root.querySelector('.nv-fh__media');
    var canvas = root.querySelector('.nv-fh__dust');
    var espresso = root.classList.contains('nv-fh--espresso');
    var ctx = canvas && canvas.getContext('2d');

    // target and current values, eased each frame
    var t = { mx: 0, my: 0, sx: 50, sy: 40 }, c = { mx: 0, my: 0, sx: 50, sy: 40 };
    var visible = false, hovering = false, raf = 0, last = 0, W = 0, H = 0, dpr = 1, parts = [];

    function size() {
      if (!canvas) return;
      var r = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = r.width; H = r.height;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var n = Math.round(Math.min(70, Math.max(28, (W * H) / 14000)));
      parts = [];
      for (var i = 0; i < n; i++) parts.push(spawn(true));
    }
    function spawn(anywhere) {
      var z = Math.random();                       // 0 = far, 1 = near
      var strand = !espresso && Math.random() < 0.14;
      return {
        x: Math.random() * W,
        y: anywhere ? Math.random() * H : H + 10,
        z: z,
        r: strand ? 0 : 0.6 + z * 2.4,
        len: strand ? 6 + z * 10 : 0,
        rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.6,
        vx: (Math.random() - 0.5) * 6,
        vy: -(4 + z * 14) * (espresso ? 0.7 : 1),   // drift upward, near ones faster
        tw: Math.random() * Math.PI * 2,
        strand: strand
      };
    }
    function drawDust(dt) {
      if (!ctx) return;
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.x += p.vx * dt; p.y += p.vy * dt; p.tw += dt * 1.6; p.rot += p.vr * dt;
        if (p.y < -12 || p.x < -20 || p.x > W + 20) { parts[i] = spawn(false); continue; }
        // parallax: near particles shift more with the pointer
        var px = p.x + c.mx * (8 + p.z * 26), py = p.y + c.my * (6 + p.z * 20);
        var a = (0.25 + p.z * 0.55) * (0.65 + 0.35 * Math.sin(p.tw));
        if (p.strand) {
          ctx.save(); ctx.translate(px, py); ctx.rotate(p.rot);
          ctx.strokeStyle = 'rgba(214,72,32,' + (a * 0.9).toFixed(3) + ')';
          ctx.lineWidth = 0.8 + p.z * 0.8; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(-p.len / 2, 0); ctx.quadraticCurveTo(0, -p.len * 0.18, p.len / 2, 0); ctx.stroke();
          ctx.restore();
        } else {
          var col = espresso ? '255,178,92' : '240,204,140';
          var g = ctx.createRadialGradient(px, py, 0, px, py, p.r * 3);
          g.addColorStop(0, 'rgba(' + col + ',' + a.toFixed(3) + ')');
          g.addColorStop(1, 'rgba(' + col + ',0)');
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(px, py, p.r * 3, 0, Math.PI * 2); ctx.fill();
        }
      }
    }

    function frame(now) {
      raf = 0;
      if (!visible) return;
      var dt = Math.min(0.05, (now - (last || now)) / 1000); last = now;
      var k = 1 - Math.exp(-dt * 6);
      c.mx += (t.mx - c.mx) * k; c.my += (t.my - c.my) * k;
      c.sx += (t.sx - c.sx) * k; c.sy += (t.sy - c.sy) * k;
      if (Math.abs(c.mx) < 0.002 && Math.abs(t.mx) < 0.002) c.mx = 0;
      if (Math.abs(c.my) < 0.002 && Math.abs(t.my) < 0.002) c.my = 0;
      var s = root.style;
      s.setProperty('--mx', c.mx.toFixed(4));
      s.setProperty('--my', c.my.toFixed(4));
      s.setProperty('--ry', (c.mx * 3).toFixed(3) + 'deg');
      s.setProperty('--rx', (-c.my * 2.4).toFixed(3) + 'deg');
      s.setProperty('--sx', c.sx.toFixed(2) + '%');
      s.setProperty('--sy', c.sy.toFixed(2) + '%');
      drawDust(dt);
      raf = requestAnimationFrame(frame);
    }
    function kick() { if (!raf && visible) { last = 0; raf = requestAnimationFrame(frame); } }

    if (finePointer) {
      root.addEventListener('pointermove', function (e) {
        hovering = true;
        var r = root.getBoundingClientRect();
        t.mx = ((e.clientX - r.left) / r.width) * 2 - 1;
        t.my = ((e.clientY - r.top) / r.height) * 2 - 1;
        var m = media.getBoundingClientRect();
        t.sx = Math.max(0, Math.min(100, ((e.clientX - m.left) / m.width) * 100));
        t.sy = Math.max(0, Math.min(100, ((e.clientY - m.top) / m.height) * 100));
      });
      root.addEventListener('pointerleave', function () { hovering = false; t.mx = 0; t.my = 0; t.sx = 50; t.sy = 40; });
    }
    // scroll depth drives the effect on touch screens; on desktop the photo rests flat (pixel-sharp) until hovered
    function onScroll() {
      var r = root.getBoundingClientRect(), vh = window.innerHeight || 1;
      var p = Math.max(-1, Math.min(1, ((r.top + r.height / 2) - vh / 2) / (vh / 2 + r.height / 2)));
      if (finePointer) { if (!hovering) { t.mx = 0; t.my = 0; t.sx = 50; t.sy = 40; } return; }
      { var q = Math.max(-1, Math.min(1, p * 2)); t.my = q; t.mx = Math.sin(q * 1.6) * 0.4; t.sy = 30 + (q + 1) * 20; t.sx = 50 + q * 18; }
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    if (io) new IntersectionObserver(function (es) {
      visible = es[0].isIntersecting;
      if (visible) { onScroll(); kick(); }
    }, { rootMargin: '80px 0px' }).observe(root);
    else { visible = true; kick(); }

    size();
    var to;
    window.addEventListener('resize', function () { clearTimeout(to); to = setTimeout(size, 150); });
  }

  function boot() { document.querySelectorAll('.nv-fh').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  // theme editor: sections re-render
  document.addEventListener('shopify:section:load', boot);
})();
